import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { analyticsService, type TrendData, type AnalyticsData, type AnalyticsFilters, type IncidentAnalytics, type InvestigationAnalytics, type ThreatAnalytics, type ExecutiveProtectionAnalytics, type SlaStatus, type GeographicAnalytics } from '@/services/analytics.service';

// Animation variants for module transitions - defined outside component
const MODULE_VARIANTS = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.2, ease: 'easeOut' },
  },
};
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  UserGroupIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  EyeIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import InsightsPanel from '@/components/analytics/InsightsPanel';
import RiskGauge from '@/components/analytics/RiskGauge';
import IncidentFunnel from '@/components/analytics/IncidentFunnel';
import AnalyticsMap from '@/components/analytics/AnalyticsMap';
import IncidentLocationChart from '@/components/analytics/IncidentLocationChart';
import GeographicDataQualityFooter from '@/components/analytics/GeographicDataQuality';
import AdaptiveTimeSeriesChart from '@/components/analytics/AdaptiveTimeSeriesChart';
import { DateInput } from '@/components/ui/DateInput';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ComposedChart,
  Line,
} from 'recharts';

interface MetricCardProps {
  title: string;
  metric: TrendData;
  icon: React.ComponentType<{ className?: string }>;
  color?: 'primary' | 'danger' | 'warning' | 'success';
  isPositiveTrend?: boolean;
  hideTrend?: boolean;
  slaStatus?: SlaStatus;
  unit?: string;
  tooltip?: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  metric,
  icon: Icon,
  color = 'primary',
  isPositiveTrend = true,
  hideTrend = false,
  slaStatus,
  unit: _unit,
  tooltip: _tooltip,
}) => {
  const safeMetric = metric ?? { current: 0, previous: 0, change: 0, changePercent: 0, trend: 'stable' as const };

  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400',
    danger: 'bg-danger-100 text-danger-600 dark:bg-danger-900/20 dark:text-danger-400',
    warning: 'bg-warning-100 text-warning-600 dark:bg-warning-900/20 dark:text-warning-400',
    success: 'bg-success-100 text-success-600 dark:bg-success-900/20 dark:text-success-400',
  };

  const getTrendIcon = () => {
    if (safeMetric.trend === 'stable') { return null; }

    const isTrendPositive = isPositiveTrend ? safeMetric.trend === 'up' : safeMetric.trend === 'down';
    const IconComponent = safeMetric.trend === 'up' ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
    const colorClass = isTrendPositive
      ? 'text-success-600 dark:text-success-400'
      : 'text-danger-600 dark:text-danger-400';

    return <IconComponent className={`w-4 h-4 ${colorClass}`} />;
  };

  const getTrendColor = () => {
    if (safeMetric.trend === 'stable') { return 'text-secondary-600 dark:text-secondary-400'; }

    const isTrendPositive = isPositiveTrend ? safeMetric.trend === 'up' : safeMetric.trend === 'down';
    return isTrendPositive
      ? 'text-success-600 dark:text-success-400'
      : 'text-danger-600 dark:text-danger-400';
  };

  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
              {title}
            </p>
            <p className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-white truncate">
              {analyticsService.formatNumber(safeMetric.current)}
            </p>
            {!hideTrend && (
              <div className="flex items-center gap-1 mt-1 min-w-0">
                {getTrendIcon()}
                <span className={`text-xs sm:text-sm shrink-0 ${getTrendColor()}`}>
                  {analyticsService.formatPercentage(safeMetric.changePercent)}
                </span>
                <span className="text-xs text-secondary-500 dark:text-secondary-400 truncate hidden sm:inline">
                  vs previous period
                </span>
              </div>
            )}
            {slaStatus && (
              <div className={`mt-2 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center max-w-full overflow-hidden ${
                slaStatus.status === 'good'
                  ? 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-400'
                  : slaStatus.status === 'warning'
                  ? 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400'
                  : 'bg-danger-100 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400'
              }`}>
                <span className={`w-2 h-2 rounded-full mr-1.5 shrink-0 ${
                  slaStatus.status === 'good' ? 'bg-success-500' :
                  slaStatus.status === 'warning' ? 'bg-warning-500' : 'bg-danger-500'
                }`} />
                <span className="truncate">
                  SLA: {slaStatus.status === 'good' ? 'On Target' :
                        slaStatus.status === 'warning' ? 'At Risk' : 'Breached'}
                </span>
                <span className="truncate hidden sm:inline">
                  {` (${slaStatus.isLowerBetter ? '\u2264' : '\u2265'}${slaStatus.thresholdGood}${slaStatus.unit === 'hours' ? 'h' : 'd'})`}
                </span>
              </div>
            )}
          </div>
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center shrink-0 ${colorClasses[color]}`}>
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface ChartCardProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
  data?: unknown[];
}

const ChartCard: React.FC<ChartCardProps> = ({ title, description, children, actions, data }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between">
        <div>
          <CardTitle>{title}</CardTitle>
          {description && (
            <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
              {description}
            </p>
          )}
        </div>
        {actions}
      </div>
    </CardHeader>
    <CardContent>
      {data?.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[300px] text-center">
          <ChartBarIcon className="w-12 h-12 text-secondary-300 dark:text-secondary-600 mb-3" />
          <p className="text-sm font-medium text-secondary-500 dark:text-secondary-400">
            No data available
          </p>
          <p className="text-xs text-secondary-400 dark:text-secondary-500 mt-1">
            Data will appear once records have been created
          </p>
        </div>
      ) : (
        children
      )}
    </CardContent>
  </Card>
);

// VERACODE-MITIGATION: CWE-80 - React auto-escapes JSX output; no dangerouslySetInnerHTML used here.
// All dynamic values are rendered via JSX expressions which are escaped by React's rendering engine.
const Analytics: React.FC = () => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [moduleData, setModuleData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<AnalyticsFilters>({
    timeRange: '30d'
  });
  const [selectedModule, setSelectedModule] = useState<string>('overview');

  const timeRangeOptions = analyticsService.getTimeRangeOptions();

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (selectedModule === 'overview') {
        const data = await analyticsService.getAnalyticsData(filters);
        setAnalyticsData(data);
      } else if (selectedModule === 'incidents') {
        const data = await analyticsService.getIncidentAnalytics(filters);
        setModuleData(prev => ({ ...prev, incidents: data }));
      } else if (selectedModule === 'investigations') {
        const data = await analyticsService.getInvestigationAnalytics(filters);
        setModuleData(prev => ({ ...prev, investigations: data }));
      } else if (selectedModule === 'threats') {
        const data = await analyticsService.getThreatAnalytics(filters);
        setModuleData(prev => ({ ...prev, threats: data }));
      } else if (selectedModule === 'executive') {
        const data = await analyticsService.getExecutiveProtectionAnalytics(filters);
        setModuleData(prev => ({ ...prev, executiveProtection: data }));
      } else if (selectedModule === 'geographic') {
        const data = await analyticsService.getGeographicAnalytics();
        setModuleData(prev => ({ ...prev, geographic: data }));
      }
    } catch (err) {
      console.error('Failed to fetch analytics data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  }, [filters, selectedModule]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Helper to get module data - prefers module-specific fetch, falls back to comprehensive
  const getIncidents = (): IncidentAnalytics | null =>
    (moduleData.incidents as IncidentAnalytics) ?? analyticsData?.incidents ?? null;
  const getInvestigations = (): InvestigationAnalytics | null =>
    (moduleData.investigations as InvestigationAnalytics) ?? analyticsData?.investigations ?? null;
  const getThreats = (): ThreatAnalytics | null =>
    (moduleData.threats as ThreatAnalytics) ?? analyticsData?.threats ?? null;
  const getExecutiveProtection = (): ExecutiveProtectionAnalytics | null =>
    (moduleData.executiveProtection as ExecutiveProtectionAnalytics) ?? analyticsData?.executiveProtection ?? null;

  const findSla = (statuses: SlaStatus[] | undefined, key: string) =>
    statuses?.find(s => s.metricKey === key);

  const handleTimeRangeChange = (timeRange: string) => {
    setFilters(prev => ({ ...prev, timeRange }));
  };

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      const exportOptions = {
        format,
        includeCharts: true,
        includeRawData: true,
        timeRange: filters.timeRange ?? '30d',
        filters
      };

      const blob = await analyticsService.exportAnalytics(exportOptions);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const ext = format === 'excel' ? 'xlsx' : format;
      link.download = `security-analytics-${new Date().toISOString().split('T')[0]}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export analytics:', err);
    }
  };

  const chartColors = analyticsService.getChartColors();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload?.length) {
      return (
        <div className="bg-white dark:bg-secondary-800 p-4 border border-primary-200 dark:border-primary-700/50 rounded-lg shadow-xl">
          <p className="text-sm font-semibold text-secondary-900 dark:text-white mb-2 pb-2 border-b border-secondary-200 dark:border-secondary-700">{label}</p>
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between space-x-4 mt-1">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                <span className="text-sm text-secondary-700 dark:text-secondary-300">{entry.name}:</span>
              </div>
              <span className="text-sm font-semibold" style={{ color: entry.color }}>{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const navigate = useNavigate();

  const handleDrillDown = (path: string, params: Record<string, string>) => {
    const searchParams = new URLSearchParams(params);
    navigate(`/${path}?${searchParams.toString()}`);
  };

  const moduleOptions = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'incidents', name: 'Incidents', icon: ExclamationTriangleIcon },
    { id: 'investigations', name: 'Investigations', icon: MagnifyingGlassIcon },
    { id: 'threats', name: 'Threats', icon: EyeIcon },
    { id: 'executive', name: 'Executive Protection', icon: ShieldCheckIcon },
    { id: 'geographic', name: 'Geographic', icon: MapPinIcon },
  ];

  if (loading) {
    return (
      <div className="p-4 sm:p-6">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  const hasData = selectedModule === 'overview'
    ? analyticsData !== null
    : Object.keys(moduleData).length > 0 || analyticsData !== null;

  if (error ?? !hasData) {
    return (
      <div className="p-4 sm:p-6">
        <div className="text-center py-8">
          <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <p className="text-danger-600 dark:text-danger-400">{error ?? 'Failed to load analytics data'}</p>
          <Button variant="outline" className="mt-4" onClick={() => fetchAnalyticsData()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            Analytics & Reports
          </h1>
          <p className="text-secondary-600 dark:text-secondary-400">
            Comprehensive insights and visual reports across all security operations
          </p>
        </div>

        <div className="space-y-4">
          {/* Time Range and Primary Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-3">
              <CalendarIcon className="w-5 h-5 text-secondary-500" />
              <select
                value={filters.timeRange}
                onChange={(e) => handleTimeRangeChange(e.target.value)}
                className="min-w-[160px] px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white"
              >
                {timeRangeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {filters.timeRange === 'custom' && (
                <div className="flex items-center space-x-2">
                  <DateInput
                    value={filters.dateRange?.startDate ?? ''}
                    onChange={(value) => setFilters(prev => ({
                      ...prev,
                      dateRange: { startDate: value, endDate: prev.dateRange?.endDate ?? '' }
                    }))}
                    className="w-44"
                  />
                  <span className="text-secondary-500">to</span>
                  <DateInput
                    value={filters.dateRange?.endDate ?? ''}
                    onChange={(value) => setFilters(prev => ({
                      ...prev,
                      dateRange: { startDate: prev.dateRange?.startDate ?? '', endDate: value }
                    }))}
                    className="w-44"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setFilters({ timeRange: '30d' })}
                className="flex items-center space-x-2"
              >
                <FunnelIcon className="w-4 h-4" />
                <span>Reset Filters</span>
              </Button>

              <div className="relative">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      handleExport(e.target.value as 'pdf' | 'excel' | 'csv');
                      e.target.value = '';
                    }
                  }}
                  className="appearance-none px-4 py-2 pr-8 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white cursor-pointer"
                  defaultValue=""
                >
                  <option value="" disabled>Export Data</option>
                  <option value="pdf" disabled>📄 PDF Report (Coming Soon)</option>
                  <option value="excel">📊 Excel Spreadsheet</option>
                  <option value="csv">📋 CSV Data</option>
                </select>
                <DocumentArrowDownIcon className="w-4 h-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-secondary-400 pointer-events-none" />
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Module Navigation */}
      <div className="flex overflow-x-auto space-x-1 pb-2">
        {moduleOptions.map((module) => {
          const Icon = module.icon;
          return (
            <button
              key={module.id}
              onClick={() => setSelectedModule(module.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${selectedModule === module.id
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                : 'text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:text-white dark:hover:bg-secondary-800'
                }`}
            >
              <Icon className="w-4 h-4" />
              <span>{module.name}</span>
            </button>
          );
        })}
      </div>

      {/* Overview Module */}
      {selectedModule === 'overview' && analyticsData && (
        <motion.div
          variants={MODULE_VARIANTS}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Automated Insights */}
          {analyticsData.insights && analyticsData.insights.length > 0 && (
            <InsightsPanel insights={analyticsData.insights} />
          )}

          {/* Risk Posture Dashboard */}
          {analyticsData.riskPosture && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white flex items-center space-x-2">
                  <ShieldCheckIcon className="w-5 h-5 text-primary-500" />
                  <span>Security Risk Posture</span>
                </h3>
                <div className="group relative">
                  <button className="flex items-center space-x-1 text-xs text-secondary-500 hover:text-primary-600 dark:text-secondary-400 dark:hover:text-primary-400 transition-colors">
                    <InformationCircleIcon className="w-4 h-4" />
                    <span>Methodology</span>
                  </button>
                  <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute right-0 top-full mt-1 z-50 w-80 p-3 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg text-xs text-secondary-600 dark:text-secondary-300">
                    <p className="font-medium text-secondary-900 dark:text-white mb-2">How the score is calculated</p>
                    <p className="mb-2">Composite score (0–100, higher = worse) is a weighted sum of four domain scores:</p>
                    <ul className="space-y-1 mb-2">
                      <li><span className="font-medium">Incidents (30%)</span> — critical ratio, open rate, MTTR</li>
                      <li><span className="font-medium">Threats (30%)</span> — avg risk score, critical ratio, active ratio</li>
                      <li><span className="font-medium">Investigations (20%)</span> — overdue ratio, case load, resolution time</li>
                      <li><span className="font-medium">Exec. Protection (20%)</span> — high-risk persons, activity distribution</li>
                    </ul>
                    <p className="text-secondary-400 dark:text-secondary-500">Domains with no data contribute 0 and do not inflate the score.</p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left: Risk Gauge */}
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-6">
                    <RiskGauge
                      score={analyticsData.riskPosture.compositeScore}
                      previousScore={analyticsData.riskPosture.previousScore}
                      size={200}
                    />
                    {/* Score breakdown by domain */}
                    <div className="w-full mt-3 pt-3 border-t border-secondary-100 dark:border-secondary-700 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      {Object.entries(analyticsData.riskPosture.scoreBreakdown).map(([key, domain]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-secondary-500 dark:text-secondary-400 capitalize">{key === 'executiveProtection' ? 'Exec. Prot.' : key}</span>
                          <span className="font-medium text-secondary-700 dark:text-secondary-300">{domain.score}<span className="text-secondary-400 dark:text-secondary-500">/{Math.round(domain.weight * 100)}%</span></span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Center: MTTR/MTTD KPIs */}
                <div className="space-y-3">
                  <MetricCard
                    title="Mean Time to Resolve"
                    metric={analyticsData.riskPosture.kpis.mttr}
                    icon={ClockIcon}
                    color="warning"
                    isPositiveTrend={false}
                    unit="h"
                    tooltip="MTTR: average hours from incident creation to resolution. Lower is better."
                  />
                  <MetricCard
                    title="Mean Time to Detect"
                    metric={analyticsData.riskPosture.kpis.mttd}
                    icon={ClockIcon}
                    color="primary"
                    isPositiveTrend={false}
                    unit="h"
                    tooltip="MTTD: average hours between incident occurrence and system detection."
                  />
                  {/* Benchmark indicators */}
                  <div className="flex space-x-4 text-xs text-secondary-500 dark:text-secondary-400 px-1">
                    <span>MTTR Benchmark: {analyticsData.riskPosture.kpis.mttrBenchmark}h</span>
                    <span>MTTD Benchmark: {analyticsData.riskPosture.kpis.mttdBenchmark}h</span>
                  </div>
                </div>

                {/* Right: Risk by Domain */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Risk by Domain</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analyticsData.riskPosture.riskByDomain.map((domain) => (
                        <div key={domain.name} className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: domain.color }} />
                            <span className="text-sm text-secondary-700 dark:text-secondary-300">{domain.name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-secondary-900 dark:text-white">{domain.value}</span>
                            <span className="text-xs text-secondary-500">({domain.percentage}%)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Risks */}
              {analyticsData.riskPosture.topRisks.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Top Active Risks</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {analyticsData.riskPosture.topRisks.map((risk, index) => {
                        const severityColors: Record<string, string> = {
                          CRITICAL: 'bg-danger-100 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400',
                          HIGH: 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400',
                          MEDIUM: 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
                        };
                        return (
                          <div key={index} className="flex items-start space-x-3 p-2 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-800/50">
                            <span className="text-sm font-medium text-secondary-400 w-5">{index + 1}.</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-secondary-900 dark:text-white">{risk.title}</span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityColors[risk.severity] ?? severityColors.MEDIUM}`}>
                                  {risk.severity}
                                </span>
                              </div>
                              <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">{risk.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Key Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
            <MetricCard
              title="Total Incidents"
              metric={analyticsData.incidents.totalIncidents}
              icon={ExclamationTriangleIcon}
              color="danger"
              isPositiveTrend={false}
            />
            <MetricCard
              title="Active Investigations"
              metric={analyticsData.investigations.activeInvestigations}
              icon={MagnifyingGlassIcon}
              color="warning"
              isPositiveTrend={false}
            />
            <MetricCard
              title="Active Threats"
              metric={analyticsData.threats.activeThreats}
              icon={ShieldExclamationIcon}
              color="danger"
              isPositiveTrend={false}
            />
            <MetricCard
              title="Protected Persons"
              metric={analyticsData.executiveProtection.protectedPersons}
              icon={UserGroupIcon}
              color="success"
            />
            <MetricCard
              title="Overdue Cases"
              metric={analyticsData.investigations.overdueCases}
              icon={ExclamationTriangleIcon}
              color="danger"
              isPositiveTrend={false}
            />
          </div>

          {/* Overview Charts — one per module */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Incidents by Status */}
            <ChartCard
              title="Incidents by Status"
              description="Current distribution of incident statuses"
              data={analyticsData.incidents.incidentsByStatus}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.incidents.incidentsByStatus}>
                  <defs>
                    <linearGradient id="overviewIncidentBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColors[0]} stopOpacity={1} />
                      <stop offset="100%" stopColor={chartColors[0]} stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Bar
                    dataKey="value"
                    fill="url(#overviewIncidentBarGradient)"
                    radius={[8, 8, 0, 0]}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('incidents', { status: data.name });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Investigations by Status */}
            <ChartCard
              title="Investigations by Status"
              description="Current distribution of investigation statuses"
              data={analyticsData.investigations.investigationsByStatus}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analyticsData.investigations.investigationsByStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    label={({ name, percentage }) => `${name} ${Math.round(percentage)}%`}
                    outerRadius={90}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('investigations', { status: data.name });
                    }}
                  >
                    {analyticsData.investigations.investigationsByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderCustomTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Total Threats */}
            <ChartCard
              title="Total Threats"
              description="Threat totals over time"
              data={analyticsData.threats.threatsTrend}
            >
              <AdaptiveTimeSeriesChart
                data={analyticsData.threats.threatsTrend}
                color={chartColors[2]}
                gradientId="overviewThreatGradient"
                originalType="area"
                renderTooltip={renderCustomTooltip}
              />
            </ChartCard>

            {/* Activities by Type */}
            <ChartCard
              title="Activities by Type"
              description="Breakdown of protection activities"
              data={analyticsData.executiveProtection.activitiesByType}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.executiveProtection.activitiesByType}>
                  <defs>
                    <linearGradient id="overviewExecBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColors[4]} stopOpacity={1} />
                      <stop offset="100%" stopColor={chartColors[4]} stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Bar
                    dataKey="value"
                    fill="url(#overviewExecBarGradient)"
                    radius={[8, 8, 0, 0]}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('executive-protection', { type: data.name });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

        </motion.div>
      )}

      {/* Incidents Module */}
      {selectedModule === 'incidents' && (() => {
        const incidents = getIncidents();
        if (!incidents) {return null;}
        return (
        <motion.div
          variants={MODULE_VARIANTS}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Incident Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
            <MetricCard
              title="Total Incidents"
              metric={incidents.totalIncidents}
              icon={ExclamationTriangleIcon}
              color="danger"
              isPositiveTrend={false}
            />
            <MetricCard
              title="Critical Incidents"
              metric={incidents.criticalIncidents}
              icon={ExclamationTriangleIcon}
              color="danger"
              isPositiveTrend={false}
            />
            <MetricCard
              title="Avg Resolution Time"
              metric={incidents.averageResolutionTime}
              icon={ChartBarIcon}
              color="primary"
              isPositiveTrend={false}
              unit="h"
              tooltip="Mean Time to Resolve (MTTR): average hours from incident creation to resolution. Lower is better."
              slaStatus={findSla(incidents.slaStatuses, 'averageResolutionTime')}
            />
            <MetricCard
              title="Avg Detection Time"
              metric={incidents.averageDetectionTime}
              icon={ClockIcon}
              color="warning"
              isPositiveTrend={false}
              unit="h"
              tooltip="Mean Time to Detect (MTTD): average hours between incident occurrence and system detection. Clamped at 0 for late-reported incidents."
              slaStatus={findSla(incidents.slaStatuses, 'averageDetectionTime')}
            />
            <MetricCard
              title="Avg Closure Time"
              metric={incidents.averageClosureTime}
              icon={ClockIcon}
              color="primary"
              isPositiveTrend={false}
              unit="h"
              tooltip="Mean Time to Close (MTTC): average hours from incident creation to closure approval. Includes resolution + closure workflow time."
              slaStatus={findSla(incidents.slaStatuses, 'averageClosureTime')}
            />
          </div>

          {/* Incident Lifecycle Funnel */}
          {incidents.lifecycle && incidents.lifecycle.stages.length > 0 && (
            <IncidentFunnel data={incidents.lifecycle} />
          )}

          {/* Incident Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard
              title="Incidents by Status"
              description="Current distribution of incident statuses"
              data={incidents.incidentsByStatus}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={incidents.incidentsByStatus}>
                  <defs>
                    <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColors[0]} stopOpacity={1} />
                      <stop offset="100%" stopColor={chartColors[0]} stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Bar
                    dataKey="value"
                    fill="url(#barGradient)"
                    radius={[8, 8, 0, 0]}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('incidents', { status: data.name });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Incidents by Severity"
              description="Breakdown by severity levels"
              data={incidents.incidentsBySeverity}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={incidents.incidentsBySeverity}
                    cx="50%"
                    cy="50%"
                    labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    label={({ name, percentage }) => `${name} ${Math.round(percentage)}%`}
                    outerRadius={90}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('incidents', { severity: data.name });
                    }}
                  >
                    {incidents.incidentsBySeverity.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderCustomTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Incident Trends"
              description="Incidents reported over time"
              data={incidents.incidentsTrend}
            >
              <AdaptiveTimeSeriesChart
                data={incidents.incidentsTrend}
                color={chartColors[0]}
                gradientId="incidentTrendGradient"
                originalType="area"
                renderTooltip={renderCustomTooltip}
              />
            </ChartCard>

            <ChartCard
              title="Resolution Time Trends"
              description="Average resolution time over time (hours)"
              data={incidents.resolutionTimeTrend}
            >
              <AdaptiveTimeSeriesChart
                data={incidents.resolutionTimeTrend}
                color={chartColors[1]}
                gradientId="resolutionTimeTrendGradient"
                originalType="line"
                renderTooltip={renderCustomTooltip}
              />
            </ChartCard>

            <ChartCard
              title="Detection Time Trends"
              description="Average time to detect over time (hours)"
              data={incidents.detectionTimeTrend}
            >
              <AdaptiveTimeSeriesChart
                data={incidents.detectionTimeTrend}
                color={chartColors[3]}
                gradientId="detectionTimeTrendGradient"
                originalType="line"
                renderTooltip={renderCustomTooltip}
              />
            </ChartCard>

            <ChartCard
              title="Closure Time Trends"
              description="Average time to close over time (hours)"
              data={incidents.closureTimeTrend}
            >
              <AdaptiveTimeSeriesChart
                data={incidents.closureTimeTrend}
                color={chartColors[0]}
                gradientId="closureTimeTrendGradient"
                originalType="line"
                renderTooltip={renderCustomTooltip}
              />
            </ChartCard>
          </div>
        </motion.div>
        );
      })()}

      {/* Investigations Module */}
      {selectedModule === 'investigations' && (() => {
        const investigations = getInvestigations();
        if (!investigations) {return null;}
        return (
        <motion.div
          variants={MODULE_VARIANTS}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Investigation Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <MetricCard
              title="Total Investigations"
              metric={investigations.totalInvestigations}
              icon={MagnifyingGlassIcon}
              color="primary"
            />
            <MetricCard
              title="Active Investigations"
              metric={investigations.activeInvestigations}
              icon={MagnifyingGlassIcon}
              color="warning"
            />
            <MetricCard
              title="Avg Investigation Time"
              metric={investigations.averageInvestigationTime}
              icon={ChartBarIcon}
              color="primary"
              isPositiveTrend={false}
              slaStatus={findSla(investigations.slaStatuses, 'averageInvestigationTime')}
            />
            <MetricCard
              title="Overdue Cases"
              metric={investigations.overdueCases}
              icon={ExclamationTriangleIcon}
              color="danger"
              isPositiveTrend={false}
            />
          </div>

          {/* Investigation Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard
              title="Investigations by Status"
              description="Current distribution of investigation statuses"
              data={investigations.investigationsByStatus}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={investigations.investigationsByStatus}
                    cx="50%"
                    cy="50%"
                    labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    label={({ name, percentage }) => `${name} ${Math.round(percentage)}%`}
                    outerRadius={90}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('investigations', { status: data.name });
                    }}
                  >
                    {investigations.investigationsByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderCustomTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Investigation Trends"
              description="Investigation cases over time"
              data={investigations.investigationsTrend}
            >
              <AdaptiveTimeSeriesChart
                data={investigations.investigationsTrend}
                color={chartColors[2]}
                gradientId="investigationTrendGradient"
                originalType="area"
                renderTooltip={renderCustomTooltip}
              />
            </ChartCard>

            <ChartCard
              title="Investigations by Priority"
              description="Current distribution by priority level"
              data={investigations.investigationsByPriority}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={investigations.investigationsByPriority}>
                  <defs>
                    <linearGradient id="invPriorityBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColors[1]} stopOpacity={1} />
                      <stop offset="100%" stopColor={chartColors[1]} stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Bar
                    dataKey="value"
                    fill="url(#invPriorityBarGradient)"
                    radius={[8, 8, 0, 0]}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('investigations', { priority: data.name });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Investigations by Category"
              description="Breakdown by investigation type"
              data={investigations.investigationsByCategory}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={investigations.investigationsByCategory}
                    cx="50%"
                    cy="50%"
                    labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    label={({ name, percentage }) => `${name} ${Math.round(percentage)}%`}
                    outerRadius={90}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                    animationDuration={300}
                    animationEasing="ease-out"
                  >
                    {investigations.investigationsByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderCustomTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Opened vs. Closed"
              description="Cases opened and closed per period with net backlog trend"
              data={investigations.openedVsClosed}
            >
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={investigations.openedVsClosed}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    yAxisId="counts"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    yAxisId="backlog"
                    orientation="right"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                    label={{ value: 'Net Backlog', angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#94a3b8' } }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Legend />
                  <Bar
                    yAxisId="counts"
                    dataKey="opened"
                    name="Opened"
                    fill="#f97316"
                    radius={[4, 4, 0, 0]}
                    animationDuration={300}
                    animationEasing="ease-out"
                  />
                  <Bar
                    yAxisId="counts"
                    dataKey="closed"
                    name="Closed"
                    fill="#22c55e"
                    radius={[4, 4, 0, 0]}
                    animationDuration={300}
                    animationEasing="ease-out"
                  />
                  <Line
                    yAxisId="backlog"
                    type="monotone"
                    dataKey="netBacklog"
                    name="Net Backlog"
                    stroke="#6366f1"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                    animationDuration={300}
                    animationEasing="ease-out"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </motion.div>
        );
      })()}

      {/* Threats Module */}
      {selectedModule === 'threats' && (() => {
        const threats = getThreats();
        if (!threats) {return null;}
        return (
        <motion.div
          variants={MODULE_VARIANTS}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Threat Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <MetricCard
              title="Total Threats"
              metric={threats.totalThreats}
              icon={ShieldExclamationIcon}
              color="danger"
              isPositiveTrend={false}
            />
            <MetricCard
              title="Active Threats"
              metric={threats.activeThreats}
              icon={ExclamationTriangleIcon}
              color="danger"
              isPositiveTrend={false}
            />
            <Card>
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm font-medium text-secondary-600 dark:text-secondary-400 truncate">
                      Risk Score
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-white truncate">
                      {threats.riskScoring?.averageRiskScore ?? 0}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1 truncate">
                      {threats.riskScoring?.totalAssessed ?? 0} assessed
                    </p>
                  </div>
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center shrink-0 ${
                    (threats.riskScoring?.averageRiskScore ?? 0) >= 76 ? 'bg-danger-100 text-danger-600 dark:bg-danger-900/20 dark:text-danger-400' :
                    (threats.riskScoring?.averageRiskScore ?? 0) >= 51 ? 'bg-danger-100 text-danger-600 dark:bg-danger-900/20 dark:text-danger-400' :
                    (threats.riskScoring?.averageRiskScore ?? 0) >= 26 ? 'bg-warning-100 text-warning-600 dark:bg-warning-900/20 dark:text-warning-400' :
                    'bg-success-100 text-success-600 dark:bg-success-900/20 dark:text-success-400'
                  }`}>
                    <ShieldExclamationIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Threat Activity */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-4">Recent Threat Activity</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-white">{threats.recentTrends?.last24h ?? 0}</p>
                  <p className="text-xs sm:text-sm text-secondary-500 dark:text-secondary-400">Last 24 Hours</p>
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-white">{threats.recentTrends?.last7d ?? 0}</p>
                  <p className="text-xs sm:text-sm text-secondary-500 dark:text-secondary-400">Last 7 Days</p>
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold text-secondary-900 dark:text-white">{threats.recentTrends?.last30d ?? 0}</p>
                  <p className="text-xs sm:text-sm text-secondary-500 dark:text-secondary-400">Last 30 Days</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Threat Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard
              title="Threats by Severity"
              description="Distribution by threat level"
              data={threats.threatsBySeverity}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={threats.threatsBySeverity}>
                  <defs>
                    <linearGradient id="threatBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColors[2]} stopOpacity={1} />
                      <stop offset="100%" stopColor={chartColors[2]} stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Bar
                    dataKey="value"
                    fill="url(#threatBarGradient)"
                    radius={[8, 8, 0, 0]}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('threat-intelligence', { threatLevel: data.name });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Threat Types"
              description="Breakdown by threat categories"
              data={threats.threatsByType}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={threats.threatsByType}
                    cx="50%"
                    cy="50%"
                    labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    label={({ name, percentage }) => `${name} ${Math.round(percentage)}%`}
                    outerRadius={90}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('threat-intelligence', { category: data.name });
                    }}
                  >
                    {threats.threatsByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderCustomTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            {(() => {
              const RISK_LEVEL_COLORS: Record<string, string> = {
                critical: '#DC2626',
                high: '#EF4444',
                medium: '#F59E0B',
                low: '#10B981',
              };
              const riskLevelData = Object.entries(threats.riskScoring?.byRiskLevel ?? {})
                .filter(([, value]) => value > 0)
                .map(([name, value]) => ({
                  name,
                  value,
                  color: RISK_LEVEL_COLORS[name] ?? '#94a3b8',
                  percentage: 0,
                }));

              return (
                <ChartCard
                  title="Risk Level Distribution"
                  description="Current risk level of each assessed threat (latest calculation only)"
                  data={riskLevelData}
                  actions={
                    <div className="relative group">
                      <InformationCircleIcon className="w-5 h-5 text-secondary-400 cursor-help" />
                      <div className="absolute right-0 top-6 z-50 hidden group-hover:block w-72 p-3 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg text-xs text-secondary-600 dark:text-secondary-300">
                        <p className="font-medium mb-1">Risk Level Distribution</p>
                        <p>Shows the number of threat assessments at each computed risk level (low, medium, high, critical). Only the most recent risk calculation per threat is counted, so reassessments do not inflate totals.</p>
                      </div>
                    </div>
                  }
                >
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={riskLevelData}
                        cx="50%"
                        cy="50%"
                        labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                        label={({ name, value }) => `${name} (${value})`}
                        outerRadius={90}
                        innerRadius={40}
                        fill="#8884d8"
                        dataKey="value"
                        stroke="#fff"
                        strokeWidth={2}
                        animationDuration={300}
                        animationEasing="ease-out"
                      >
                        {riskLevelData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={renderCustomTooltip} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              );
            })()}
          </div>
        </motion.div>
        );
      })()}

      {/* Executive Protection Module */}
      {selectedModule === 'executive' && (() => {
        const ep = getExecutiveProtection();
        if (!ep) {return null;}
        return (
        <motion.div
          variants={MODULE_VARIANTS}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          {/* Executive Protection Metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <MetricCard
              title="Total Activities"
              metric={ep.totalActivities}
              icon={CalendarIcon}
              color="success"
            />
            <MetricCard
              title="Protected Persons"
              metric={ep.protectedPersons}
              icon={UserGroupIcon}
              color="success"
            />
            <MetricCard
              title="Security Events"
              metric={ep.securityEvents}
              icon={ShieldCheckIcon}
              color="warning"
              isPositiveTrend={false}
            />
          </div>

          {/* Executive Protection Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard
              title="Activities by Type"
              description="Breakdown of protection activities"
              data={ep.activitiesByType}
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={ep.activitiesByType}>
                  <defs>
                    <linearGradient id="executiveBarGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColors[4]} stopOpacity={1} />
                      <stop offset="100%" stopColor={chartColors[4]} stopOpacity={0.7} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" strokeOpacity={0.5} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickLine={{ stroke: '#cbd5e1' }}
                    axisLine={{ stroke: '#cbd5e1' }}
                  />
                  <Tooltip content={renderCustomTooltip} />
                  <Bar
                    dataKey="value"
                    fill="url(#executiveBarGradient)"
                    radius={[8, 8, 0, 0]}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('executive-protection', { type: data.name });
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard
              title="Events by Risk Level"
              description="Security events categorized by risk"
              data={ep.eventsByRiskLevel}
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={ep.eventsByRiskLevel}
                    cx="50%"
                    cy="50%"
                    labelLine={{ stroke: '#94a3b8', strokeWidth: 1 }}
                    label={({ name, percentage }) => `${name} ${Math.round(percentage)}%`}
                    outerRadius={90}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                    animationDuration={300}
                    animationEasing="ease-out"
                    cursor="pointer"
                    onClick={(data: { name: string }) => {
                      handleDrillDown('executive-protection', { riskLevel: data.name });
                    }}
                  >
                    {ep.eventsByRiskLevel.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={renderCustomTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </motion.div>
        );
      })()}

      {/* Geographic Module */}
      {selectedModule === 'geographic' && (() => {
        const geographic = moduleData.geographic as GeographicAnalytics | undefined;
        if (!geographic) {return null;}
        return (
          <motion.div
            variants={MODULE_VARIANTS}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            <AnalyticsMap data={geographic} />
            <IncidentLocationChart data={geographic.incidentLocations} />
            {geographic.dataQuality && (
              <GeographicDataQualityFooter data={geographic.dataQuality} />
            )}
          </motion.div>
        );
      })()}


    </div>
  );
};

export default Analytics;